import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    preOrderItems: 0,
    totalBookings: 0,
    activePromotions: 0,
    averageOrderValue: 0,
    conversionRate: 0
  });

  const [trends, setTrends] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    completedOrders: 0,
    pendingOrders: 0,
    totalBookings: 0,
    averageOrderValue: 0
  });
  
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [activePromotions, setActivePromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockIssuesCount, setStockIssuesCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatTrend = (value) => {
    if (value === 0 || value === undefined || isNaN(value)) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${Math.round(value)}%`;
  };

  const getTrendStyle = (value) => {
    if (value > 0) return { color: 'var(--success)', icon: '📈' };
    if (value < 0) return { color: 'var(--error)', icon: '📉' };
    return { color: 'var(--text-light)', icon: '➡️' };
  };

  // Status helper functions
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'var(--warning)';
      case 'completed': return 'var(--success)';
      case 'cancelled': return 'var(--error)';
      case 'processing': return 'var(--info)';
      case 'shipped': return 'var(--primary)';
      case 'delivered': return 'var(--success)';
      default: return 'var(--text-light)';
    }
  };

  const getStatusDisplay = (status, isSmallScreen = false) => {
    const statusMap = {
      'pending': { text: 'Pending', icon: '⏳', short: 'Pend' },
      'completed': { text: 'Completed', icon: '', short: 'Comp' },
      'cancelled': { text: 'Cancelled', icon: '', short: 'Canc' },
      'processing': { text: 'Processing', icon: '⚙️', short: 'Proc' },
      'shipped': { text: 'Shipped', icon: '🚚', short: 'Ship' },
      'delivered': { text: 'Delivered', icon: '📦', short: 'Delv' }
    };
    
    const statusInfo = statusMap[status?.toLowerCase()] || { 
      text: status || 'Unknown', 
      icon: '❓', 
      short: status?.substring(0, 4) || 'Unkn' 
    };
    
    return {
      ...statusInfo,
      displayText: isSmallScreen ? statusInfo.short : statusInfo.text,
      color: getStatusColor(status)
    };
  };

  // Stock helper functions
  const calculateStockStatus = (product) => {
    const availableStock = (product.inventory?.stock || 0) - (product.inventory?.reservedStock || 0);
    const threshold = product.inventory?.lowStockThreshold || 10;
    
    if (product.productType === 'pre-order' || product.productType === 'growing' || product.stockStatus === 'pre-order') {
      return 'pre-order';
    }
    
    if (availableStock <= 0) return 'out-of-stock';
    if (availableStock <= threshold) return 'low-stock';
    return 'in-stock';
  };

  const getStockStatusColor = (stockStatus) => {
    switch (stockStatus) {
      case 'low-stock': return 'var(--warning)';
      case 'out-of-stock': return 'var(--error)';
      case 'pre-order': return 'var(--info)';
      case 'in-stock': return 'var(--success)';
      default: return 'var(--text-light)';
    }
  };

  const getStockStatusDisplay = (stockStatus, isSmallScreen = false) => {
    const statusMap = {
      'low-stock': { text: 'Low Stock', short: 'Low' },
      'out-of-stock': { text: 'Out of Stock', short: 'Out' },
      'pre-order': { text: 'Pre-Order', short: 'Pre' },
      'in-stock': { text: 'In Stock', short: 'In' }
    };
    
    const statusInfo = statusMap[stockStatus] || { 
      text: stockStatus || 'Unknown', 
      short: stockStatus?.substring(0, 3) || 'Unk' 
    };
    
    return {
      ...statusInfo,
      displayText: isSmallScreen ? statusInfo.short : statusInfo.text
    };
  };

  // Navigation functions
  const navigateToOrders = () => navigate('/admin/orders');
  const navigateToProducts = () => navigate('/admin/products');
  const navigateToNewProduct = () => navigate('/admin/products/new');
  const navigateToPromotions = () => navigate('/admin/promotions');
  const navigateToNewPromotion = () => navigate('/admin/promotions/new');
  const navigateToUsers = () => navigate('/admin/users');

  // Data fetching
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        toast.error('Please login to view dashboard');
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const [
        ordersStatsResponse,
        productsResponse,
        recentOrdersResponse,
        promotionsResponse,
        trendsResponse
      ] = await Promise.all([
        fetch(`${API_URL}/orders/admin/stats`, { headers }).catch(() => ({})),
        fetch(`${API_URL}/admin/products?limit=100`, { headers }).catch(() => ({})),
        fetch(`${API_URL}/orders/admin?limit=5`, { headers }).catch(() => ({})),
        fetch(`${API_URL}/promotions`, { headers }).catch(() => ({})),
        fetch(`${API_URL}/analytics/trends`, { headers }).catch(() => ({}))
      ]);

      const ordersStatsData = ordersStatsResponse.ok ? await ordersStatsResponse.json() : {};
      const productsData = productsResponse.ok ? await productsResponse.json() : {};
      const recentOrdersData = recentOrdersResponse.ok ? await recentOrdersResponse.json() : {};
      const promotionsData = promotionsResponse.ok ? await promotionsResponse.json() : {};
      const trendsData = trendsResponse.ok ? await trendsResponse.json() : {};

      const products = productsData.data || productsData.products || productsData || [];

      const productsWithAvailableStock = products.map(product => ({
        ...product,
        availableStock: (product.inventory?.stock || 0) - (product.inventory?.reservedStock || 0)
      }));

      // Calculate stock status for each product
      const productsWithCalculatedStatus = productsWithAvailableStock.map(product => ({
        ...product,
        calculatedStockStatus: calculateStockStatus(product)
      }));

      const lowStockProductsList = productsWithCalculatedStatus.filter(product => {
        return product.calculatedStockStatus === 'low-stock';
      });

      const outOfStockProducts = productsWithCalculatedStatus.filter(product => {
        return product.calculatedStockStatus === 'out-of-stock';
      });

      const preOrderProducts = productsWithCalculatedStatus.filter(product => {
        return product.calculatedStockStatus === 'pre-order';
      });

      const totalBookings = productsWithCalculatedStatus.reduce((total, product) => {
        if (product.productType === 'pre-order' || product.productType === 'growing') {
          const preOrderQuantity = product.preOrders?.reduce((sum, preOrder) => 
            sum + (preOrder.quantity || 0), 0) || 0;
          return total + preOrderQuantity;
        }
        return total;
      }, 0);

      const now = new Date();
      const activePromos = (promotionsData.data || promotionsData.promotions || promotionsData || []).filter(promo => {
        const startDate = new Date(promo.startDate);
        const endDate = new Date(promo.endDate);
        return promo.status === 'active' || (startDate <= now && endDate >= now);
      });

      const totalRevenue = ordersStatsData.data?.totalRevenue || ordersStatsData.totalRevenue || 0;
      const totalOrders = ordersStatsData.data?.totalOrders || ordersStatsData.totalOrders || 0;
      const completedOrders = ordersStatsData.data?.completedOrders || ordersStatsData.completedOrders || 0;
      const averageOrderValue = completedOrders > 0 ? totalRevenue / completedOrders : 0;
      const conversionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;

      setStats({
        totalOrders,
        pendingOrders: ordersStatsData.data?.pendingOrders || ordersStatsData.pendingOrders || 0,
        completedOrders,
        cancelledOrders: ordersStatsData.data?.cancelledOrders || ordersStatsData.cancelledOrders || 0,
        totalRevenue,
        totalProducts: products.length,
        lowStockItems: lowStockProductsList.length,
        outOfStockItems: outOfStockProducts.length,
        preOrderItems: preOrderProducts.length,
        totalBookings,
        activePromotions: activePromos.length,
        averageOrderValue,
        conversionRate
      });

      setTrends(trendsData.data || {
        totalRevenue: 0,
        totalOrders: 0,
        completedOrders: 0,
        pendingOrders: 0,
        totalBookings: 0,
        averageOrderValue: 0
      });

      const ordersArray = recentOrdersData.data || recentOrdersData.orders || recentOrdersData || [];
      setRecentOrders(Array.isArray(ordersArray) ? ordersArray.slice(0, 5) : []);

      // FIXED: Show same items on all screens (up to limits)
      const MAX_STOCK_ITEMS = 10;
      const MAX_PROMOTIONS = 6;
      
      setLowStockProducts(lowStockProductsList.slice(0, MAX_STOCK_ITEMS));
      setActivePromotions(activePromos.slice(0, MAX_PROMOTIONS));

    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [isMobile, isSmallScreen]);

  const statsData = [
    { 
      label: 'Total Revenue', 
      value: `KSh ${stats.totalRevenue?.toLocaleString() || '0'}`, 
      color: 'var(--success)', 
      icon: '💰',
      description: 'Lifetime revenue',
      trend: trends.totalRevenue,
      key: 'totalRevenue'
    },
    { 
      label: 'Total Orders', 
      value: stats.totalOrders, 
      color: 'var(--accent-gold)', 
      icon: '📦',
      description: 'All time orders',
      trend: trends.totalOrders,
      key: 'totalOrders'
    },
    { 
      label: 'Active Bookings', 
      value: stats.totalBookings, 
      color: 'var(--info)', 
      icon: '📅',
      description: 'Pre-orders & bookings',
      trend: trends.totalBookings,
      key: 'totalBookings'
    },
    { 
      label: 'Pending Orders', 
      value: stats.pendingOrders, 
      color: 'var(--warning)', 
      icon: '⏳',
      description: 'Awaiting processing',
      trend: trends.pendingOrders,
      key: 'pendingOrders'
    },
    { 
      label: 'Completed Orders', 
      value: stats.completedOrders, 
      color: 'var(--success)', 
      icon: '✅',
      description: 'Delivered orders',
      trend: trends.completedOrders,
      key: 'completedOrders'
    },
    { 
      label: 'Avg Order Value', 
      value: `KSh ${Math.round(stats.averageOrderValue)?.toLocaleString() || '0'}`, 
      color: 'var(--primary)', 
      icon: '📊',
      description: 'Avg revenue per order',
      trend: trends.averageOrderValue,
      key: 'averageOrderValue'
    },
    { 
      label: 'Stock Issues', 
      value: stockIssuesCount, 
      color: 'var(--error)', 
      icon: '⚠️',
      description: 'Orders needing fix'
    },
  ];

  const checkStockIssues = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${API_URL}/admin/stock-orders/stock-issues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStockIssuesCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error checking stock issues:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    checkStockIssues();
  }, []);

  const fixStockIssues = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      toast.loading('Fixing stock issues...');
      
      const response = await fetch(`${API_URL}/admin/orders/fix-all-stock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.dismiss();
      
      if (response.ok) {
        toast.success('Stock issues fixed!');
        fetchDashboardData();
        checkStockIssues();
      } else {
        toast.error('Failed to fix stock issues');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error fixing stock issues:', error);
      toast.error('Error fixing stock issues');
    }
  };

  const formatDate = (dateString) => {
    try {
      if (isSmallScreen) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      }
      if (isMobile) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <h1 className="accent-gold" style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-3xl)'),
          marginBottom: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)')
        }}>
          Dashboard Overview
        </h1>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : (isMobile ? 'column' : 'row'),
          alignItems: isSmallScreen ? 'flex-start' : (isMobile ? 'flex-start' : 'center'),
          gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          justifyContent: 'space-between'
        }}>
          <p style={{ 
            color: 'var(--text-medium)',
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
          }}>
            Welcome to <span className="text-gold" style={{ fontWeight: '600' }}>Bloomsoko</span> Admin Panel
          </p>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
          }}>
            <button 
              onClick={fetchDashboardData}
              className="btn btn-secondary"
              disabled={loading}
              style={{ 
                padding: isSmallScreen ? '6px 10px' : (isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
            
            {!loading && !isSmallScreen && (
              <span style={{ 
                fontSize: 'var(--font-size-xs)', 
                color: 'var(--text-light)',
                display: isMobile ? 'none' : 'inline'
              }}>
                Updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Section */}
      <div style={{ marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') }}>
        <h2 style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)'),
          marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          color: 'var(--text-dark)'
        }}>
          Key Metrics
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 
                               isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
        }}>
          {statsData.map((stat, index) => {
            const trendStyle = getTrendStyle(stat.trend);
            const trendDisplay = formatTrend(stat.trend);
            
            return (
              <div key={stat.key || index} className="stat-card gold" style={{
                padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                minHeight: isSmallScreen ? '100px' : (isMobile ? '120px' : '140px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-2)'
                }}>
                  <div>
                    <div className="stat-value" style={{ 
                      color: stat.color,
                      fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
                      lineHeight: 1.2,
                      marginBottom: 'var(--space-1)',
                      wordBreak: 'break-word'
                    }}>
                      {stat.value}
                    </div>
                    <div style={{
                      color: 'var(--text-dark)',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>
                      {isSmallScreen && stat.label.length > 12 ? stat.label.substring(0, 10) + '...' : stat.label}
                    </div>
                    <div style={{
                      fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                      color: 'var(--text-light)',
                      lineHeight: 1.3,
                      display: isSmallScreen ? 'none' : 'block'
                    }}>
                      {isMobile ? stat.description?.split(' ').slice(0, 3).join(' ') + '...' : stat.description}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: isSmallScreen ? '1.2rem' : (isMobile ? '1.5rem' : '1.75rem'),
                    opacity: 0.8
                  }}>
                    {stat.icon}
                  </div>
                </div>
                
                {trendDisplay && (
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: trendStyle.color,
                    fontWeight: '600',
                    padding: '2px 6px',
                    backgroundColor: trendStyle.color === 'var(--success)' ? 'rgba(34, 197, 94, 0.1)' : 
                                   trendStyle.color === 'var(--error)' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                    borderRadius: 'var(--radius-sm)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    width: 'fit-content'
                  }}>
                    <span style={{ fontSize: '0.75rem' }}>{trendStyle.icon}</span>
                    {trendDisplay}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stock & Promotions Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
        gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)')
      }}>
        {/* Stock Management Card */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              📦 Stock Management
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
            maxHeight: isSmallScreen ? '250px' : (isMobile ? '300px' : '400px'),
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: isSmallScreen ? '150px' : '200px'
          }}>
            {/* Stock Summary Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : (isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'),
              gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
              marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)'
            }}>
              {[
                { label: 'Products', value: stats.totalProducts, color: 'var(--primary)', icon: '📦' },
                { label: 'Low Stock', value: stats.lowStockItems, color: stats.lowStockItems > 0 ? 'var(--warning)' : 'var(--success)', icon: '⚠️' },
                { label: 'Out of Stock', value: stats.outOfStockItems, color: stats.outOfStockItems > 0 ? 'var(--error)' : 'var(--success)', icon: '⛔' },
                { label: 'Pre-Order', value: stats.preOrderItems, color: 'var(--info)', icon: '📅' }
              ].map((item, index) => (
                <div key={index} style={{ 
                  textAlign: 'center',
                  padding: isSmallScreen ? '8px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: `2px solid ${item.color}20`
                }}>
                  <div style={{ 
                    fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
                    color: item.color,
                    marginBottom: '4px'
                  }}>
                    {item.icon}
                  </div>
                  <div style={{ 
                    fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'), 
                    fontWeight: '700', 
                    color: item.color,
                    marginBottom: '2px'
                  }}>
                    {item.value}
                  </div>
                  <div style={{ 
                    fontSize: isSmallScreen ? '10px' : (isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)'), 
                    color: 'var(--text-medium)',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Low Stock Alerts */}
            {lowStockProducts.length > 0 ? (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-3)',
                  paddingBottom: 'var(--space-2)',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <h4 style={{ 
                    margin: 0,
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                    color: 'var(--text-dark)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: 'var(--warning)' }}>⚠️</span>
                    Low Stock Alerts
                  </h4>
                  <span className="badge-gold" style={{
                    fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                    padding: isSmallScreen ? '1px 5px' : '2px 6px'
                  }}>
                    {lowStockProducts.length}
                  </span>
                </div>
                
                <div style={{ 
                  borderRadius: 'var(--radius-md)'
                }}>
                  {lowStockProducts.map((product, index) => {
                    const availableStock = product.availableStock || 0;
                    const stockStatus = product.calculatedStockStatus || calculateStockStatus(product);
                    const statusDisplay = getStockStatusDisplay(stockStatus, isSmallScreen);
                    
                    return (
                      <div key={product._id || index} style={{
                        padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3)'),
                        borderBottom: index < lowStockProducts.length - 1 ? '1px solid var(--border-light)' : 'none',
                        backgroundColor: index % 2 === 0 ? 'var(--bg-light)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 'var(--space-2)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: index < lowStockProducts.length - 1 ? '4px' : '0'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: 'var(--text-dark)',
                            fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                            marginBottom: '4px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {product.name || 'Unnamed Product'}
                          </div>
                          <div style={{ 
                            fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                            color: 'var(--text-light)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>
                              Available: <strong style={{ 
                                color: stockStatus === 'out-of-stock' ? 'var(--error)' : 
                                       stockStatus === 'low-stock' ? 'var(--warning)' : 
                                       'var(--success)' 
                              }}>{availableStock}</strong>
                            </span>
                            {product.inventory?.lowStockThreshold && (
                              <span>• Threshold: {product.inventory.lowStockThreshold}</span>
                            )}
                          </div>
                        </div>
                        <span style={{
                          backgroundColor: getStockStatusColor(stockStatus),
                          color: 'white',
                          padding: isSmallScreen ? '4px 8px' : '6px 12px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          minWidth: isSmallScreen ? '40px' : '70px',
                          textAlign: 'center'
                        }}>
                          {statusDisplay.displayText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)', 
                color: 'var(--text-light)'
              }}>
                <div style={{ fontSize: isSmallScreen ? '1.5rem' : '2rem', marginBottom: 'var(--space-2)', color: 'var(--success)' }}>✅</div>
                <p style={{ 
                  fontWeight: '600', 
                  color: 'var(--text-dark)',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  Stock levels are good
                </p>
                <p style={{ 
                  fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                  marginTop: 'var(--space-1)'
                }}>
                  All products have sufficient stock
                </p>
              </div>
            )}

            {/* Stock Issues Alert */}
            {stockIssuesCount > 0 && (
              <div style={{ 
                marginTop: 'var(--space-3)',
                padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--error)',
                borderRadius: 'var(--radius-md)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-2)'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      marginBottom: '2px'
                    }}>
                      <span style={{ color: 'var(--error)', fontSize: isSmallScreen ? '0.9rem' : '1rem' }}>⚠️</span>
                      <div style={{ 
                        fontWeight: '600', 
                        color: 'var(--error)',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
                      }}>
                        {isSmallScreen ? 'Stock Issues' : 'Stock Issues Detected'}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                      color: 'var(--text-light)'
                    }}>
                      {stockIssuesCount} order{stockIssuesCount !== 1 ? 's' : ''} need{stockIssuesCount === 1 ? 's' : ''} attention
                    </div>
                  </div>
                  <button 
                    onClick={fixStockIssues}
                    className="btn"
                    style={{
                      background: 'var(--error)',
                      color: 'white',
                      border: 'none',
                      padding: isSmallScreen ? '4px 8px' : '6px 12px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = 'var(--error-dark)'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'var(--error)'}
                  >
                    {isSmallScreen ? 'Fix' : 'Fix Now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active Promotions Card */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              🎯 Active Promotions
            </h3>
            <span style={{
              backgroundColor: 'var(--accent-gold)',
              color: 'var(--text-dark)',
              padding: isSmallScreen ? '2px 8px' : '4px 12px',
              borderRadius: 'var(--radius-sm)',
              fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
              fontWeight: '600'
            }}>
              {stats.activePromotions} Active
            </span>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
            maxHeight: isSmallScreen ? '250px' : (isMobile ? '300px' : '400px'),
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: isSmallScreen ? '150px' : '200px'
          }}>
            {activePromotions.length > 0 ? (
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: isSmallScreen ? '8px' : 'var(--space-3)'
              }}>
                {activePromotions.map((promo, index) => {
                  const daysLeft = promo.endDate ? Math.ceil((new Date(promo.endDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  const isExpiringSoon = daysLeft !== null && daysLeft <= 3;
                  
                  return (
                    <div key={promo._id || index} style={{
                      padding: isSmallScreen ? '12px' : 'var(--space-3)',
                      backgroundColor: 'var(--bg-light)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.2s ease'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 'var(--space-2)',
                        marginBottom: isSmallScreen ? '8px' : 'var(--space-2)'
                      }}>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {promo.title || 'Untitled Promotion'}
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          flexShrink: 0
                        }}>
                          {isExpiringSoon && (
                            <span style={{
                              backgroundColor: 'var(--warning)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '10px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}>
                              {isSmallScreen ? '!' : 'Expiring'}
                            </span>
                          )}
                          <span style={{
                            backgroundColor: 'var(--success)',
                            color: 'white',
                            padding: isSmallScreen ? '2px 6px' : '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            Active
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ 
                        fontSize: isSmallScreen ? '10px' : 'var(--font-size-sm)', 
                        color: 'var(--text-medium)',
                        marginBottom: isSmallScreen ? '4px' : 'var(--space-1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ 
                          backgroundColor: 'rgba(255, 193, 7, 0.1)',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--accent-gold)',
                          fontWeight: '600'
                        }}>
                          {promo.type ? promo.type.replace('_', ' ').toUpperCase() : 'PROMO'}
                        </span>
                        
                        {promo.discountValue && (
                          <span style={{ 
                            color: 'var(--accent-gold)', 
                            fontWeight: '600',
                            fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                          }}>
                            {promo.discountValue}
                            {promo.discountType === 'percentage' ? '% OFF' : ' KSh OFF'}
                          </span>
                        )}
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                        color: 'var(--text-light)',
                        marginTop: '6px'
                      }}>
                        <div>
                          Ends: {promo.endDate ? formatDate(promo.endDate) : 'No end date'}
                        </div>
                        {daysLeft !== null && (
                          <div style={{ 
                            color: isExpiringSoon ? 'var(--error)' : 'var(--text-light)',
                            fontWeight: isExpiringSoon ? '600' : '400'
                          }}>
                            {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left` : 'Expired'}
                          </div>
                        )}
                      </div>
                      
                      {promo.description && !isSmallScreen && (
                        <div style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--text-medium)',
                          marginTop: '8px',
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {promo.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)', 
                color: 'var(--text-light)'
              }}>
                <div style={{ 
                  fontSize: isSmallScreen ? '1.5rem' : '2rem', 
                  marginBottom: 'var(--space-2)',
                  opacity: 0.5
                }}>
                  🎯
                </div>
                <p style={{ 
                  fontWeight: '600', 
                  color: 'var(--text-dark)',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                  marginBottom: 'var(--space-1)'
                }}>
                  No active promotions
                </p>
                <p style={{ 
                  fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                  marginBottom: 'var(--space-2)'
                }}>
                  Create promotions to boost sales
                </p>
                <button 
                  onClick={navigateToNewPromotion}
                  className="btn btn-gold"
                  style={{ 
                    padding: isSmallScreen ? '6px 12px' : '8px 16px',
                    fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                  }}
                >
                  Create Promotion
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders Card */}
      <div className="card card-gold" style={{ marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') }}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: isSmallScreen ? 'wrap' : (isMobile ? 'wrap' : 'nowrap'),
          gap: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : '0')
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            📦 Recent Orders ({recentOrders.length})
          </h3>
          <button 
            onClick={navigateToOrders}
            className="btn btn-secondary"
            style={{ 
              padding: isSmallScreen ? '4px 8px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
            }}
          >
            View All
          </button>
        </div>
        
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              Loading recent orders...
            </div>
          ) : recentOrders.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              <p style={{ fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-base)' }}>No recent orders</p>
              <p style={{ fontSize: isSmallScreen ? '9px' : 'var(--font-size-sm)' }}>Orders appear as customers place them</p>
            </div>
          ) : isSmallScreen ? (
            // Extra small screen view
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {recentOrders.map((order) => {
                const statusInfo = getStatusDisplay(order.status, true);
                
                return (
                  <div key={order._id} style={{
                    padding: '12px',
                    backgroundColor: 'var(--bg-light)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '12px',
                    alignItems: 'start'
                  }}>
                    {/* Status Badge */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <div style={{ 
                        backgroundColor: statusInfo.color,
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        minWidth: '45px'
                      }}>
                        {statusInfo.displayText}
                      </div>
                      <div style={{ 
                        fontSize: '9px', 
                        color: 'var(--text-light)',
                        textAlign: 'center'
                      }}>
                        {order.createdAt ? formatDate(order.createdAt) : ''}
                      </div>
                    </div>
                    
                    {/* Order Details */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      <div style={{ 
                        fontWeight: '700', 
                        color: 'var(--text-dark)',
                        fontSize: 'var(--font-size-xs)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        #{order.orderNumber || 'N/A'}
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        fontSize: '10px'
                      }}>
                        <span style={{ color: 'var(--text-medium)' }}>Customer:</span>
                        <span style={{ fontWeight: '500' }}>
                          {order.recipient?.firstName?.charAt(0) || 'C'}. {order.recipient?.lastName?.charAt(0) || ''}
                        </span>
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        fontSize: '10px'
                      }}>
                        <span style={{ color: 'var(--text-medium)' }}>Amount:</span>
                        <span style={{ fontWeight: '700', color: 'var(--accent-gold)' }}>
                          KSh {order.totalAmount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      
                      {order.recipient?.phone && (
                        <div style={{ 
                          fontSize: '9px', 
                          color: 'var(--text-light)',
                          marginTop: '2px'
                        }}>
                          📞 {order.recipient.phone}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isMobile ? (
            // Mobile view: Card layout
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {recentOrders.map((order) => {
                const statusInfo = getStatusDisplay(order.status, false);
                
                return (
                  <div key={order._id} style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--bg-light)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 'var(--space-2)'
                    }}>
                      <div style={{ 
                        fontWeight: '700', 
                        color: 'var(--text-dark)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        #{order.orderNumber || 'N/A'}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px'
                      }}>
                        <span style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          {order.createdAt ? formatDate(order.createdAt) : ''}
                        </span>
                        <span style={{ 
                          backgroundColor: statusInfo.color,
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600'
                        }}>
                          {statusInfo.displayText}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '6px',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-medium)' }}>Customer:</span>
                        <span style={{ fontWeight: '500' }}>
                          {order.recipient?.firstName || 'Customer'} {order.recipient?.lastName || ''}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-medium)' }}>Amount:</span>
                        <span style={{ fontWeight: '700', color: 'var(--accent-gold)' }}>
                          KSh {order.totalAmount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      
                      {order.recipient?.email && (
                        <div style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--text-light)',
                          marginTop: '2px'
                        }}>
                          📧 {order.recipient.email}
                        </div>
                      )}
                      
                      {order.recipient?.phone && (
                        <div style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--text-light)'
                        }}>
                          📞 {order.recipient.phone}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop view: Table layout with horizontal scroll
            <div style={{ 
              overflowX: 'auto',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-light)'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: '600px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--border-light)',
                    backgroundColor: 'var(--bg-light)'
                  }}>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Order #</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Customer</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Amount</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => {
                    const statusInfo = getStatusDisplay(order.status, false);
                    
                    return (
                      <tr key={order._id} style={{ 
                        borderBottom: '1px solid var(--border-light)',
                        transition: 'background-color 0.2s ease'
                      }}>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)', 
                          fontWeight: '700',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          #{order.orderNumber || 'N/A'}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div style={{ fontWeight: '500', fontSize: 'var(--font-size-sm)' }}>
                            {order.recipient?.firstName || 'Customer'} {order.recipient?.lastName || ''}
                          </div>
                          <div style={{ 
                            fontSize: 'var(--font-size-xs)', 
                            color: 'var(--text-light)',
                            marginTop: '2px'
                          }}>
                            {order.recipient?.email || 'No email'}
                          </div>
                          {order.recipient?.phone && (
                            <div style={{ 
                              fontSize: 'var(--font-size-xs)', 
                              color: 'var(--text-light)',
                              marginTop: '2px'
                            }}>
                              📞 {order.recipient.phone}
                            </div>
                          )}
                        </td>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)', 
                          fontWeight: '700', 
                          color: 'var(--accent-gold)',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          KSh {order.totalAmount?.toLocaleString() || '0'}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ fontSize: '0.8rem' }}>
                              {statusInfo.icon}
                            </span>
                            <span style={{ 
                              backgroundColor: statusInfo.color,
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}>
                              {statusInfo.displayText}
                            </span>
                          </div>
                        </td>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)', 
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-medium)'
                        }}>
                          {order.createdAt ? formatDate(order.createdAt) : 'Unknown date'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="card card-gold">
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            ⚡ Quick Actions
          </h3>
        </div>
        
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : (isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))'),
            gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
          }}>
            <button 
              onClick={navigateToOrders}
              className="btn btn-secondary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4) var(--space-5)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                minHeight: isSmallScreen ? '40px' : '50px',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '1.1em' }}>📦</span>
              <span>Orders</span>
            </button>
            
            <button 
              onClick={navigateToProducts}
              className="btn btn-secondary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4) var(--space-5)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                minHeight: isSmallScreen ? '40px' : '50px',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '1.1em' }}>🛍️</span>
              <span>Products</span>
            </button>
            
            <button 
              onClick={navigateToUsers}
              className="btn btn-secondary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4) var(--space-5)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                minHeight: isSmallScreen ? '40px' : '50px',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '1.1em' }}>👥</span>
              <span>Users</span>
            </button>
            
            <button 
              onClick={navigateToPromotions}
              className="btn btn-secondary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4) var(--space-5)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                minHeight: isSmallScreen ? '40px' : '50px',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{ fontSize: '1.1em' }}>🎯</span>
              <span>Promotions</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;